// shared/supabase/client.ts
//
// Factory — NOT a singleton. Each platform constructs its own instance.
// Web: web/src/app/supabaseClient.ts calls createRTLSClient(url, anonKey)
// Mobile (M0.5): src/app/supabaseClient.ts calls createRTLSClient(url, anonKey)
//
// The factory lives in shared/ so both platforms import identical types
// and channel-name constants without duplicating them.

import { createClient, SupabaseClient } from '@supabase/supabase-js';

// ─── Hand-stubbed Database type (replace with generated types in M0.5) ───────
// TODO M0.5: run `supabase gen types typescript --project-id <id>` and
//            replace this entire block with the generated output.

export interface Database {
  public: {
    Tables: {
      mines: {
        Row: {
          id: string;               // uuid
          name: string;
          // SQL default is '{}' — consumers must handle the empty-object case.
          // Partial<...> reflects that: a row may be missing any/all fields.
          view_config: Partial<{
            cameraPosition: [number, number, number];
            cameraTarget:   [number, number, number];
            gridPosition:   [number, number, number];
            gridSize:       number;
            gridDivisions:  number;
          }>;
          default_map_file: string | null;
          created_at: string;       // ISO 8601
        };
        Insert: Omit<Database['public']['Tables']['mines']['Row'], 'id' | 'created_at'> & {
          id?: string;
          created_at?: string;
        };
        Update: Partial<Database['public']['Tables']['mines']['Insert']>;
      };

      positions: {
        Row: {
          id: string;               // uuid
          asset_id: string;         // TEXT — references assets.id (no FK; assets is a view)
          x: number;
          y: number;
          z: number;
          timestamp: string;        // ISO 8601
        };
        Insert: Omit<Database['public']['Tables']['positions']['Row'], 'id'> & {
          id?: string;
        };
        Update: Partial<Database['public']['Tables']['positions']['Insert']>;
      };

      // Unified schema: locations are the conceptual zones from TagBoard +
      // the renderable waypoints from RTLS. type and x/y/z are NULLABLE
      // because TagBoard creates locations like "Top Cut" that aren't 3D.
      // RTLS clients should filter `WHERE x IS NOT NULL AND type IS NOT NULL`
      // when rendering.
      locations: {
        Row: {
          id: string;
          mine_id: string;          // uuid → mines.id
          name: string;
          type: 'entry' | 'tunnel' | 'station' | 'safety' | 'infrastructure' | 'stope' | 'surface' | null;
          x: number | null;
          y: number | null;
          z: number | null;
          level: number | null;
          // TagBoard-owned mining fields (RTLS reads but does not write):
          zone: string | null;
          description: string | null;
          priority: 'CRITICAL' | 'HIGH' | 'MEDIUM' | null;
          next_blast_date: string | null;
          tonnes_remaining: number | null;
          material: string | null;
          heading_status: string | null;
          created_by: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['locations']['Row'], 'created_at' | 'updated_at'> & {
          created_at?: string;
          updated_at?: string;
        };
        Update: Partial<Database['public']['Tables']['locations']['Insert']>;
      };

      // Access points: WiFi/BLE infrastructure that's 3D-renderable in RTLS.
      // TagBoard writes (CRUD via AddAccessPointScreen); RTLS reads + subscribes.
      access_points: {
        Row: {
          id: string;
          mine_id: string;
          location_id: string | null;  // FK to locations.id; nullable when AP is standalone
          name: string;
          mac_address: string | null;
          ssid: string | null;
          ip_address: string | null;
          status: 'active' | 'offline' | 'fault';
          is_active: boolean;           // legacy TagBoard binary toggle
          x: number | null;
          y: number | null;
          z: number | null;
          level: number | null;
          radius_m: number | null;
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['access_points']['Row'], 'created_at' | 'updated_at'> & {
          created_at?: string;
          updated_at?: string;
        };
        Update: Partial<Database['public']['Tables']['access_points']['Insert']>;
      };

      // Rescue chambers: first-class 3D-renderable safety entities.
      // TagBoard writes; RTLS reads + subscribes for live occupancy + color.
      rescue_chambers: {
        Row: {
          id: string;
          mine_id: string;
          name: string;
          location: string;             // primary location identifier ("Level 1500")
          location_description: string | null;
          capacity: number;
          current_occupancy: number;
          status: 'available' | 'full' | 'near_capacity' | 'maintenance';
          maintenance_status: string | null;
          last_inspection_date: string | null;
          installation_date: string | null;
          access_point: string | null;  // legacy text field — name/id of nearby AP
          x: number;                    // NOT NULL — chambers must be 3D-locatable
          y: number;
          z: number;
          level: number | null;
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['rescue_chambers']['Row'], 'created_at' | 'updated_at'> & {
          created_at?: string;
          updated_at?: string;
        };
        Update: Partial<Database['public']['Tables']['rescue_chambers']['Insert']>;
      };

      // Chamber occupancy log: RTLS reads to compute live chamber counts.
      chamber_personnel: {
        Row: {
          id: string;
          chamber_id: string;           // FK → rescue_chambers.id
          personnel_id: string;         // FK → personnel.id
          entered_at: string;
        };
        Insert: Database['public']['Tables']['chamber_personnel']['Row'];
        Update: Partial<Database['public']['Tables']['chamber_personnel']['Row']>;
      };
    };

    // The `assets` view UNIONs personnel + equipment in the shape RTLS
    // expects. SELECT-only — never .insert(). For realtime, subscribe to
    // the underlying `personnel` and `equipment` tables (postgres_changes
    // does NOT fire on views).
    Views: {
      assets: {
        Row: {
          id: string;
          mine_id: string;
          name: string;
          type: 'equipment' | 'personnel';
          subtype: string | null;       // 'truck' | 'loader' | 'drill_rig' | 'rescue_vehicle' | 'worker' | 'supervisor' | 'emergency_team' | 'other'
          status: 'active' | 'idle' | 'alert' | 'offline';
          x: number | null;
          y: number | null;
          z: number | null;
          level: number | null;
          last_updated: string;
          created_at: string;
          external_id: string | null;   // employee_id (personnel) or asset_id (equipment)
        };
      };
    };
    Functions: Record<string, never>;
    Enums: {
      rtls_asset_type:    'equipment' | 'personnel';
      rtls_asset_status:  'active' | 'idle' | 'alert' | 'offline';
      rtls_location_type: 'entry' | 'tunnel' | 'station' | 'safety' | 'infrastructure' | 'stope' | 'surface';
      personnel_status:   'tagged_in' | 'tagged_out' | 'missing';
      equipment_status:   'active' | 'down' | 'maintenance' | 'idle';
      chamber_status:     'available' | 'full' | 'near_capacity' | 'maintenance';
    };
  };
}

// ─── Realtime channel-name constants ──────────────────────────────────────────
export const RTLS_BROADCAST_CHANNEL = 'rtls-broadcast' as const;
export const RTLS_DB_CHANNEL        = 'rtls-db'        as const;

// Broadcast event names
export const BROADCAST_EVENT_POSITIONS = 'positions' as const;

// ─── Factory ──────────────────────────────────────────────────────────────────

export type RTLSSupabaseClient = SupabaseClient<Database>;

/**
 * Creates a typed Supabase client for the RTLS project.
 *
 * @param url      - Supabase project URL (e.g. https://xyz.supabase.co)
 * @param anonKey  - Supabase anon public key
 *
 * Web: call once in web/src/app/supabaseClient.ts and export the singleton.
 * Mobile: call once in src/app/supabaseClient.ts (M0.5).
 */
/**
 * Factory options. `auth` lets each platform decide its own session-storage
 * strategy without touching the factory — web passes a localStorage-backed
 * config, mobile (when wired) will pass an AsyncStorage-backed one.
 */
interface CreateRTLSClientOptions {
  auth?: {
    persistSession?: boolean
    autoRefreshToken?: boolean
    detectSessionInUrl?: boolean
    storage?: unknown
  }
}

export function createRTLSClient(
  url: string,
  anonKey: string,
  options: CreateRTLSClientOptions = {},
): RTLSSupabaseClient {
  return createClient<Database>(url, anonKey, {
    auth: {
      persistSession: options.auth?.persistSession ?? false,
      autoRefreshToken: options.auth?.autoRefreshToken ?? false,
      detectSessionInUrl: options.auth?.detectSessionInUrl ?? false,
      storage: options.auth?.storage as any,
    },
    realtime: {
      // Supabase default is 10 events/s. Bump to 60 to match the RAF
      // coalescing strategy in rtlsService.ts (M2 Broadcast). Passed via
      // the untyped `params` bag because RealtimeClientOptions has no
      // top-level `eventsPerSecond` property in @supabase/supabase-js@2.49.x.
      params: {
        eventsPerSecond: 60,
      },
    },
  });
}

export type { SupabaseClient } from '@supabase/supabase-js';
